import { useEffect, useState } from "react";
import { api, ROLE_LABELS, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

function GSyncCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  function load() { api.get("/gsync/status").then((r) => setS(r.data)); }
  useEffect(() => { load(); }, []);
  async function retry() {
    setBusy(true);
    try { const { data } = await api.post("/gsync/retry"); const c = data.resynced || {}; toast.success(`Dikirim ke Google — Penerimaan ${c.receipts||0}, Mutasi ${c.movements||0}, Produksi ${c.productions||0}.`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }
  if (!s) return null;
  return (
    <Card className="p-4" data-testid="gsync-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-heading font-bold text-sm">Sinkronisasi Google Sheets (Hibrida)</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            Status: {s.active
              ? <span className="text-emerald-600 font-semibold">AKTIF</span>
              : <span className="text-amber-600 font-semibold">NON-AKTIF (env belum diisi)</span>}
            {" "}• Tersinkron {s.synced}/{s.total} • Menunggu {s.pending} • Gagal {s.failed}
          </p>
          {!s.active && <p className="text-[11px] text-muted-foreground mt-1">Isi GOOGLE_APPS_SCRIPT_EXEC_URL &amp; SHARED_SECRET pada backend, lalu restart. MongoDB tetap database utama.</p>}
        </div>
        <Button size="sm" data-testid="gsync-retry" disabled={!s.active || busy} onClick={retry}>{busy ? "Mengirim..." : "Sync ke Google (semua)"}</Button>
      </div>
    </Card>
  );
}

function ResetDbCard() {
  const [confirm, setConfirm] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  async function reset() {
    setBusy(true);
    try {
      await api.post("/admin/reset-database", { confirm, password: pw });
      toast.success("Database transaksi dikosongkan. Nomor tracking kembali ke 00001.");
      setConfirm(""); setPw("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }
  return (
    <Card className="p-4 border-red-300" data-testid="reset-db-card">
      <h4 className="font-heading font-bold text-sm text-red-700">Kosongkan Database</h4>
      <p className="text-xs text-muted-foreground mt-0.5">Menghapus SEMUA penerimaan, QC, stok, mutasi, produksi, riwayat label, dan log audit; nomor tracking kembali ke 00001. Akun pengguna dan master data (pemasok, bahan) tetap. Tidak bisa dibatalkan.</p>
      <div className="flex flex-wrap gap-2 mt-3 items-end">
        <div><Label className="text-xs">Ketik KOSONGKAN</Label><Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 w-40" data-testid="reset-confirm" /></div>
        <div><Label className="text-xs">Password admin</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 w-48" data-testid="reset-password" /></div>
        <Button variant="destructive" disabled={busy || confirm !== "KOSONGKAN" || !pw} onClick={reset} data-testid="reset-submit">{busy ? "Mengosongkan..." : "Kosongkan Database"}</Button>
      </div>
    </Card>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", role: "gudang", password: "" });
  const isAdmin = user?.role === "admin";

  function load() {
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data));
    api.get("/audit").then((r) => setAudit(r.data));
  }
  useEffect(() => { load(); }, []);

  async function save() {
    try { await api.post("/users", f); toast.success("Pengguna dibuat."); setOpen(false); setF({ email: "", name: "", role: "gudang", password: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }
  async function del(id) {
    try { await api.delete(`/users/${id}`); toast.success("Pengguna dihapus."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  return (
    <div className="space-y-5">
      <div><h1 className="font-heading text-2xl font-extrabold">Pengguna &amp; Audit Trail</h1>
        <p className="text-sm text-muted-foreground">Kelola pengguna per peran dan tinjau seluruh riwayat tindakan.</p></div>

      {isAdmin && <GSyncCard />}
      {isAdmin && <ResetDbCard />}

      <Tabs defaultValue={isAdmin ? "users" : "audit"}>
        <TabsList>
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users">Pengguna</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="tab-audit-log">Audit Trail</TabsTrigger>
        </TabsList>

        {isAdmin && (
          <TabsContent value="users">
            <Card className="p-4">
              <div className="mb-3"><Button size="sm" data-testid="add-user-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> Tambah Pengguna</Button></div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b"><th className="py-2 font-medium">Nama</th><th className="font-medium">Email</th><th className="font-medium">Peran</th><th></th></tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="py-2">{u.name}</td><td>{u.email}</td><td>{ROLE_LABELS[u.role]}</td>
                      <td className="text-right"><Button size="sm" variant="ghost" data-testid={`del-user-${u.email}`} onClick={() => del(u.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="audit">
          <Card className="p-4">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b">
                <th className="py-2 font-medium">Waktu</th><th className="font-medium">Aksi</th><th className="font-medium">Entitas</th>
                <th className="font-medium">Pengguna</th><th className="font-medium">Alasan</th>
              </tr></thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 text-xs">{fmtDateTime(a.created_at)}</td>
                    <td><span className="text-xs px-2 py-0.5 rounded bg-slate-100 font-medium">{a.action}</span></td>
                    <td className="text-xs">{a.entity}</td><td className="text-xs">{a.user_name} ({a.role})</td><td className="text-xs">{a.reason || "-"}</td>
                  </tr>
                ))}
                {audit.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Belum ada aktivitas.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tambah Pengguna</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Nama</Label><Input data-testid="user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Email</Label><Input data-testid="user-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Peran</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="user-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Kata Sandi</Label><Input data-testid="user-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="user-save" onClick={save}>Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
